import {
  type Options as RRuleOptions,
  RRule,
  rrulestr,
  type Weekday,
  type WeekdayStr,
} from 'rrule';

import type { CustomRecurrenceRule } from '../components/CustomRecurrenceModal';

/**
 * Pure RRULE <-> CustomRecurrenceRule conversion helpers.
 *
 * The same logic exists (stateful) inside `useRecurrence` (generateRRule /
 * parseRRule), which drives the create flow and the legacy (V1) edit modal.
 * These standalone, side-effect-free versions let the V2 inline recurrence
 * editor (AppointmentPropertiesTable) reuse the conversion without pulling in
 * the whole stateful hook. Keep the two in sync; consolidating onto these
 * helpers is tracked as a follow-up.
 */

const DAY_MAP: Record<WeekdayStr, Weekday> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
};

const FREQ_MAP: Record<string, number> = {
  daily: RRule.DAILY,
  weekly: RRule.WEEKLY,
  monthly: RRule.MONTHLY,
  yearly: RRule.YEARLY,
};

const WEEKDAY_TOKENS: WeekdayStr[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

export interface RecurrenceRuleResult {
  rrule: string;
  endDate: string | null;
}

/**
 * Convert the modal's CustomRecurrenceRule into an RFC-5545 RRULE string.
 * Returns null if the rule is malformed.
 */
export function customRecurrenceRuleToRRule(
  rule: CustomRecurrenceRule,
): RecurrenceRuleResult | null {
  try {
    const options: Partial<RRuleOptions> = {};

    options.freq = FREQ_MAP[rule.frequency];
    if (options.freq === undefined) return null;

    if (rule.interval > 1) {
      options.interval = rule.interval;
    }

    if (rule.frequency === 'weekly' && rule.weekDays.length > 0) {
      options.byweekday = rule.weekDays.map(day => DAY_MAP[day as WeekdayStr]);
    }

    if (rule.frequency === 'monthly') {
      if (rule.monthlyPattern === 'dayOfMonth' && rule.monthlyDay) {
        options.bymonthday = rule.monthlyDay;
      } else if (
        rule.monthlyPattern === 'dayOfWeek' &&
        rule.weekDays.length > 0 &&
        rule.monthlyDayPosition
      ) {
        const dayOfWeek = DAY_MAP[rule.weekDays[0] as WeekdayStr];
        options.byweekday = [dayOfWeek.nth(rule.monthlyDayPosition)];
      }
    }

    if (rule.endMode === 'on' && rule.endDate) {
      const endDate = new Date(rule.endDate);
      // Clear the time portion to ensure midnight UTC.
      endDate.setUTCHours(0, 0, 0, 0);
      options.until = endDate;

      return {
        rrule: new RRule(options).toString(),
        endDate: endDate.toISOString(),
      };
    }

    if (rule.endMode === 'after' && rule.endCount) {
      options.count = rule.endCount;

      return {
        rrule: new RRule(options).toString(),
        endDate: null,
      };
    }

    return {
      rrule: new RRule(options).toString(),
      endDate: null,
    };
  } catch (error) {
    console.error('Error generating custom RRULE:', error);
    return null;
  }
}

const extractWeekdays = (rule: RRule): WeekdayStr[] => {
  const byweekday = rule.origOptions.byweekday;
  if (byweekday === undefined || byweekday === null) return [];

  const days = Array.isArray(byweekday) ? byweekday : [byweekday];
  const tokens = days
    .map(day => {
      const text = day.toString();
      return WEEKDAY_TOKENS.find(token => text.includes(token));
    })
    .filter((token): token is WeekdayStr => Boolean(token));

  return tokens;
};

/**
 * Parse an existing RRULE string into the modal's CustomRecurrenceRule shape so
 * an existing series can be pre-populated when re-opening the custom editor.
 * Returns null if the string cannot be parsed.
 */
export function rRuleToCustomRecurrenceRule(rruleString: string): CustomRecurrenceRule | null {
  try {
    const rule = rrulestr(rruleString);
    if (!(rule instanceof RRule)) return null;

    let frequency = 'weekly';
    switch (rule.options.freq) {
      case RRule.DAILY:
        frequency = 'daily';
        break;
      case RRule.WEEKLY:
        frequency = 'weekly';
        break;
      case RRule.MONTHLY:
        frequency = 'monthly';
        break;
      case RRule.YEARLY:
        frequency = 'yearly';
        break;
    }

    const interval = rule.options.interval || 1;
    const parsedWeekDays = extractWeekdays(rule);
    const weekDays = parsedWeekDays.length > 0 ? parsedWeekDays : ['MO'];

    let endMode: 'never' | 'on' | 'after' = 'never';
    let endDate: string | null = null;
    let endCount: number | null = null;

    if (rule.options.count) {
      endMode = 'after';
      endCount = rule.options.count;
    } else if (rule.options.until) {
      endMode = 'on';
      endDate = rule.options.until.toISOString().split('T')[0];
    }

    let monthlyPattern: 'dayOfMonth' | 'dayOfWeek' = 'dayOfMonth';
    let monthlyDay = 1;
    let monthlyDayPosition: 1 | 2 | 3 | 4 | -1 = 1;

    if (frequency === 'monthly') {
      const bymonthday = rule.origOptions.bymonthday;
      const byweekday = rule.origOptions.byweekday;
      const bysetpos = rule.origOptions.bysetpos;
      const firstWeekday = Array.isArray(byweekday) ? byweekday[0] : byweekday;
      // The nth-weekday position is encoded either on the Weekday itself
      // (BYDAY=+2TU — what customRecurrenceRuleToRRule emits via .nth()) or as a
      // separate BYSETPOS (a server may store it that way). Check both.
      const nthFromByday =
        firstWeekday && typeof firstWeekday === 'object' ? firstWeekday.n : undefined;
      const nthFromSetpos = Array.isArray(bysetpos) ? bysetpos[0] : bysetpos;
      const nthPosition = nthFromByday ?? nthFromSetpos;

      if (bymonthday !== undefined && bymonthday !== null) {
        monthlyPattern = 'dayOfMonth';
        monthlyDay = Array.isArray(bymonthday) ? bymonthday[0] : bymonthday;
      } else if (nthPosition != null) {
        monthlyPattern = 'dayOfWeek';
        monthlyDayPosition = nthPosition as 1 | 2 | 3 | 4 | -1;
      }
    }

    return {
      frequency,
      interval,
      weekDays,
      endMode,
      endDate,
      endCount,
      monthlyPattern,
      monthlyDay,
      monthlyDayPosition,
    };
  } catch (error) {
    console.error('Error parsing RRULE:', error);
    return null;
  }
}
